/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { RuntimeHeaders } from "@fluidframework/container-runtime/internal";
import { assert } from "@fluidframework/core-utils/internal";
import { FluidHandleBase, responseToException } from "@fluidframework/runtime-utils/internal";
/**
 * This handle is used to dynamically load a Fluid object on a remote client and is created on parsing a serialized
 * FluidObjectHandle.
 * This class is used to generate an IFluidHandle when de-serializing any all handles (including handles to DDSes,
 * custom objects) that are stored in SharedObjects. The Data Store or SharedObject corresponding to the
 * IFluidHandle can be retrieved by calling `get` on it.
 */
export class RemoteFluidObjectHandle extends FluidHandleBase {
    get IFluidHandleContext() {
        return this;
    }
    /**
     * Creates a new RemoteFluidObjectHandle when parsing an IFluidHandle.
     * @param absolutePath - The absolute path to the handle from the container runtime.
     * @param routeContext - The root IFluidHandleContext that has a route to this handle.
     */
    constructor(absolutePath, routeContext) {
        super();
        this.absolutePath = absolutePath;
        this.routeContext = routeContext;
        this.isAttached = true;
        assert(absolutePath.startsWith("/"), 0x19d /* "Handles should always have absolute paths" */);
    }
    async get() {
        if (this.objectP === undefined) {
            // Add `viaHandle` header to distinguish from requests from non-handle paths.
            const request = {
                url: this.absolutePath,
                headers: { [RuntimeHeaders.viaHandle]: true },
            };
            this.objectP = this.routeContext.resolveHandle(request).then((response) => {
                if (response.mimeType === "fluid/object") {
                    const fluidObject = response.value;
                    return fluidObject;
                }
                throw responseToException(response, request);
            });
        }
        return this.objectP;
    }
    attachGraph() {
        return;
    }
    bind(handle) {
        handle.attachGraph();
    }
}
//# sourceMappingURL=remoteObjectHandle.js.map